/**
 * usePermissions Hook
 *
 * Centralized permission checking for the current user's org role.
 * Uses the server-resolved permissions array from /auth/me with a
 * local fallback map for resilience.
 *
 * Usage:
 *   const { can, canManageChannels, isAdmin } = usePermissions();
 *   if (can('channels:manage')) { ... }
 */

import { useMemo } from 'react';
import { useOrg } from '../providers/OrgProvider';
import { useAuth } from '../providers/AuthProvider';
import type { OrgRole } from '../services/auth';

/** Local fallback: role → permissions (mirrors server permissions/index.js) */
const ROLE_PERMISSIONS: Record<OrgRole, string[]> = {
  admin: [
    'messages:read',
    'messages:send',
    'analytics:view',
    'analytics:export',
    'speakers:manage',
    'tags:manage',
    'alerts:manage',
    'channels:manage',
    'groups:manage',
    'users:manage',
    'users:invite',
    'devices:manage',
    'integrations:manage',
    'org:manage'
  ],
  editor: [
    'messages:read',
    'messages:send',
    'analytics:view',
    'speakers:manage',
    'tags:manage',
    'alerts:manage'
  ],
  viewer: ['messages:read', 'analytics:view']
};

export interface UsePermissionsResult {
  /** Current org role */
  role: OrgRole | null;
  /** Resolved permissions array */
  permissions: string[];
  /** Check if user has a specific permission */
  can: (permission: string) => boolean;
  /** Whether user is a system-level super-admin */
  isSuperAdmin: boolean;
  /** Whether user is an org admin or super-admin */
  isAdmin: boolean;

  // Convenience booleans
  canSendMessages: boolean;
  canManageChannels: boolean;
  canManageGroups: boolean;
  canManageUsers: boolean;
  canInviteUsers: boolean;
  canManageDevices: boolean;
  canManageSpeakers: boolean;
  canManageTags: boolean;
  canManageAlerts: boolean;
  canManageOrg: boolean;
  canExportAnalytics: boolean;
  canManageIntegrations: boolean;
}

export function usePermissions(): UsePermissionsResult {
  const { currentOrg } = useOrg();
  const { user } = useAuth();

  return useMemo(() => {
    const role = (currentOrg?.role as OrgRole) || null;
    const isSuperAdmin = user?.role === 'super-admin';

    // Use server-provided permissions if available, otherwise fall back to local map
    const permissions: string[] = currentOrg?.permissions?.length
      ? currentOrg.permissions
      : role
        ? ROLE_PERMISSIONS[role] || []
        : [];

    const permSet = new Set(permissions);

    const can = (permission: string): boolean => {
      if (isSuperAdmin) return true;
      return permSet.has(permission);
    };

    return {
      role,
      permissions,
      can,
      isSuperAdmin,
      isAdmin: isSuperAdmin || role === 'admin',

      canSendMessages: can('messages:send'),
      canManageChannels: can('channels:manage'),
      canManageGroups: can('groups:manage'),
      canManageUsers: can('users:manage'),
      canInviteUsers: can('users:invite'),
      canManageDevices: can('devices:manage'),
      canManageSpeakers: can('speakers:manage'),
      canManageTags: can('tags:manage'),
      canManageAlerts: can('alerts:manage'),
      canManageOrg: can('org:manage'),
      canExportAnalytics: can('analytics:export'),
      canManageIntegrations: can('integrations:manage')
    };
  }, [currentOrg, user]);
}

export default usePermissions;
